import React, { useEffect, useState } from "react";
import { fetchCheckAccounts } from "../../../../services/configService";

/**
 * AL-417 — who is holding this case up.
 *
 * Originally written as an example to prove the API end to end (AL-417); adopted as the deliverable
 * by AL-455, whose home for it is the records list — that is where in-progress cases are reachable.
 *
 * The one thing the payload is shaped around: people are grouped by WHO HAS TO ACT, not by state. A
 * C1 can only chase their own client's people, so those come first and everything else is context.
 * `outstandingPeople` counts only INVITEE and C1 for the same reason — a total of everyone
 * incomplete would send the C1 after people who are not the problem, which is the failure AL-417
 * existed to remove.
 *
 * The states deliberately distinguish three things that all look like "has not logged in":
 * someone we never wrote to, someone whose account does not exist yet, and someone who was
 * contacted and has not acted. Only the last is worth chasing, and conflating them is what made the
 * old single case-level status useless.
 */

type WaitingOn = "INVITEE" | "C1" | "ARCAML" | "NOBODY";

type Role = "PRINCIPAL" | "APPROVEDUSER" | "UBO" | "TRUSTEE";

interface CheckAccount {
  fullName: string;
  roleType: Role;
  /** Every role this person holds. Use this, not roleType, to decide whether they hold the case up. */
  roles?: Role[];
  email?: string;
  accountCreated?: string;
  state: string;
  waitingOn: WaitingOn;
  waitingSince?: string;
  checkAttempts?: number;
  depth?: number;
  viaCaseName?: string;
}

interface CheckAccountsResponse {
  caseIdentifier: string;
  totalPeople: number;
  /** Holds the case up AND the C1 can act on it. Excludes approved users, who do not hold the case. */
  outstandingPeople: number;
  /** Still to complete before they can use the system, but not holding the case up. */
  pendingApprovedUsers?: number;
  hasDeeperCases: boolean;
  accounts: CheckAccount[];
}

/** Plain language. The server's enum names are for us, not for a customer's solicitor. */
const STATE_LABELS: Record<string, string> = {
  AWAITING_INVITATION_RESPONSE: "Has not opened their invitation",
  STARTED_NOT_SUBMITTED: "Signed in but has not submitted their details",
  CHECK_IN_PROGRESS: "Identity check running",
  AWAITING_OPS: "With ARCaml for review",
  COMPLETE: "Complete",
  TERMINATED: "No longer required",
  // AL-478 #3 / AL-477: the server is splitting DEDUP_LINKED out of TERMINATED.
  // Someone here is not stopped — they were verified under another role they hold
  // on this same case, so their second workflow was closed as a duplicate rather
  // than abandoned. TERMINATED keeps its own wording for people genuinely stopped.
  //
  // Shipped ahead of AL-477 deliberately: an unused key is inert, and without it
  // the row would print the raw token and fall through to the grey fallback tone.
  VERIFIED_ELSEWHERE: "Verified under their other role on this case",
  NO_CONTACT_ADDRESS: "We have no email address for this person",
  // The ordinary course, not a delay: nobody is invited until the entity's own checks reach the point
  // where the individual workflows are created. Worded so a C1 does not read it as something going wrong.
  NOT_YET_INVITED: "Not yet invited — will be contacted once the entity's own checks complete",
  // Same absence of an account, but past the point where the launcher runs. Deliberately says only
  // what the C1 can act on, which is nothing: it is with us either way, and the row already sits under
  // "Being processed or reviewed by ARCaml".
  //
  // It does NOT say "could not be created" or otherwise report a failure, for two reasons. Our own
  // internal errors are not something to advertise to a customer's solicitor. And the state is not
  // only reached by failure: ChildWorkflowLauncher deliberately skips a UBO or trustee whose
  // originating supplement_request is REJECTED or SUPERSEDED, and the query behind this panel does
  // not reproduce that exclusion, so a person excluded on purpose lands here too. Claiming a cause
  // we have not established is worse than claiming none. See AL-461.
  INVITATION_NOT_CREATED: "Not yet invited",
};

const ROLE_LABELS: Record<string, string> = {
  PRINCIPAL: "Principal",
  APPROVEDUSER: "Approved user",
  UBO: "Beneficial owner",
  TRUSTEE: "Trustee",
};

/**
 * Colour per state, so the reader can sweep the panel without reading every
 * label.
 *
 * Amber is reserved for "someone was asked and has not acted" — the only states
 * a C1 can do anything about. Blue is work in flight, green is done, grey is
 * neither: not yet invited is the ordinary course, not a delay, and colouring it
 * as one is what made an earlier version look like a list of problems.
 */
const STATE_TONES: Record<string, string> = {
  AWAITING_INVITATION_RESPONSE: "bg-amber-100 text-amber-800 ring-amber-200",
  STARTED_NOT_SUBMITTED: "bg-amber-100 text-amber-800 ring-amber-200",
  NO_CONTACT_ADDRESS: "bg-blue-100 text-blue-800 ring-blue-200",
  CHECK_IN_PROGRESS: "bg-indigo-100 text-indigo-800 ring-indigo-200",
  AWAITING_OPS: "bg-indigo-100 text-indigo-800 ring-indigo-200",
  COMPLETE: "bg-emerald-100 text-emerald-800 ring-emerald-200",
  TERMINATED: "bg-slate-100 text-slate-600 ring-slate-200",
  // Green, not grey: this person IS verified. Grey would read as "nothing
  // happened here", which is the opposite of what the state means.
  VERIFIED_ELSEWHERE: "bg-emerald-100 text-emerald-800 ring-emerald-200",
  NOT_YET_INVITED: "bg-slate-100 text-slate-600 ring-slate-200",
  INVITATION_NOT_CREATED: "bg-slate-100 text-slate-600 ring-slate-200",
};

const STATE_TONE_FALLBACK = "bg-slate-100 text-slate-600 ring-slate-200";

/**
 * Grouping is by who acts, and the order is deliberate: what the C1 can do first, what we are doing
 * second, what is finished last.
 */
/**
 * AL-456: does this person's inaction hold the case's reported status?
 *
 * Approved users do not. No workflow rule gates a parent case on them, they complete asynchronously by
 * design, and more can be added after the case is otherwise finished — and since AL-456 they no longer
 * suppress the case's reported status either.
 *
 * Everyone else does: a beneficial owner, external principal or trustee who has not finished keeps the
 * case reporting as in progress.
 *
 * Keyed on the FULL role set, not roleType. Duplicate role rows are collapsed into one person, so an
 * approved user who is also a beneficial owner survives as a single row — and they genuinely do hold the
 * case. Filing them under the non-blocking heading would be exactly the wrong answer.
 */
function holdsUpTheCase(a: CheckAccount): boolean {
  const roles = rolesOf(a);
  return roles.some((r) => r !== "APPROVEDUSER");
}

/** The full role set, falling back to the single `roleType` on an older payload. */
function rolesOf(a: CheckAccount): Role[] {
  return a.roles && a.roles.length > 0 ? a.roles : [a.roleType];
}

/**
 * AL-478 #2: what to call somebody who holds more than one role.
 *
 * `collapseDuplicatePeople` merges duplicate rows into one person carrying the
 * full `roles` set, and `holdsUpTheCase` reads that set — but the surviving row
 * keeps whichever `roleType` arrived first, and PEOPLE_SQL ends
 * `ORDER BY … pe.role_type, …` where APPROVEDUSER sorts before UBO. So a
 * beneficial owner who also holds an approved-user account was labelled
 * "Approved user" every time: the merged set was computed, used for the logic,
 * and then thrown away for display.
 *
 * The material role is the one that holds the case up, so that is what leads.
 * Any non-blocking roles follow, because "also an approved user" is true and
 * occasionally the reason someone is on the list at all — it just must not be the
 * headline.
 *
 * The interface comment already said "Use this, not roleType, to decide whether
 * they hold the case up". This applies the same rule to the label.
 */
function roleLabelsFor(a: CheckAccount): string[] {
  const roles = rolesOf(a);
  const blocking = roles.filter((r) => r !== "APPROVEDUSER");
  const ordered = blocking.length > 0 ? [...blocking, ...roles.filter((r) => r === "APPROVEDUSER")] : roles;
  // De-duplicated: a payload may repeat a role across merged rows.
  return [...new Set(ordered)].map((r) => ROLE_LABELS[r] ?? r);
}

/**
 * Each group carries its own colour, and the two the C1 can act on carry a tinted
 * card as well.
 *
 * This has now been tried both ways. Filling every group made the panel a stack
 * of competing alert boxes; stripping all of them made it a wall of grey small
 * text that looked unfinished next to the rest of the portal. Tinting only the
 * actionable groups keeps the emphasis where the payload puts it while still
 * giving the panel some substance.
 */
const GROUPS: {
  key: WaitingOn;
  heading: string;
  note: string;
  /** Wrapper for the whole group. Tinted only where the reader can act. */
  card: string;
  /** Avatar and accent colour. */
  avatar: string;
  dot: string;
}[] = [
  {
    key: "INVITEE",
    heading: "Waiting on your client's people",
    note: "These are the people to chase.",
    card: "bg-gradient-to-br from-amber-50 to-orange-50/60 border border-amber-200",
    avatar: "bg-gradient-to-br from-amber-400 to-orange-500 text-white",
    dot: "bg-amber-500",
  },
  {
    key: "C1",
    heading: "Waiting on you",
    note: "We have no way to reach these people. Send us an address and we will invite them.",
    card: "bg-gradient-to-br from-blue-50 to-indigo-50/60 border border-blue-200",
    avatar: "bg-gradient-to-br from-blue-500 to-indigo-600 text-white",
    dot: "bg-blue-500",
  },
  {
    key: "ARCAML",
    heading: "With us",
    note: "Nothing for you to do.",
    card: "bg-white border border-slate-200",
    avatar: "bg-slate-200 text-slate-600",
    dot: "bg-slate-400",
  },
  {
    key: "NOBODY",
    heading: "Finished",
    note: "",
    card: "bg-white border border-slate-200",
    avatar: "bg-gradient-to-br from-emerald-400 to-teal-500 text-white",
    dot: "bg-emerald-500",
  },
];

/** Initials for the avatar. Two at most, so a long name does not overflow it. */
function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return (first + last).toUpperCase();
}

/**
 * Long waits are the thing worth reacting to, so they are coloured rather than
 * left as quiet grey text. Two weeks is the threshold: below it a person may
 * simply not have got to it yet.
 */
const LONG_WAIT_DAYS = 14;

function waitedDays(since?: string): number | null {
  if (!since) return null;
  const started = new Date(since).getTime();
  if (Number.isNaN(started)) return null;
  return Math.floor((Date.now() - started) / 86_400_000);
}

function waitingDuration(since?: string): string | null {
  if (!since) return null;
  const started = new Date(since).getTime();
  if (Number.isNaN(started)) return null;
  const days = Math.floor((Date.now() - started) / 86_400_000);
  if (days >= 1) return `${days} day${days === 1 ? "" : "s"}`;
  const hours = Math.floor((Date.now() - started) / 3_600_000);
  if (hours >= 1) return `${hours} hour${hours === 1 ? "" : "s"}`;
  return "less than an hour";
}

/**
 * One person.
 *
 * Extracted because the blocking groups and the approved-user group rendered the
 * same markup twice and had already drifted — the second copy omitted the
 * sub-case attribution and the retry count.
 *
 * Layout puts the two facts that drive a decision on the outside: who, top left;
 * how long they have been sitting, top right. The state and role share a quieter
 * second line, and the email is a mailto link rather than grey text, because
 * chasing someone is the whole point of reading this and it was previously the
 * least legible thing on the row.
 *
 * Names are printed exactly as stored. The registry supplies them uppercase and
 * it is tempting to title-case them, but "O'BRIEN" and "MCDONALD" do not survive
 * that transformation, and mangling a legal name on a compliance record is worse
 * than leaving it shouting.
 */
const PersonRow: React.FC<{ account: CheckAccount; avatarClass: string }> = ({
  account: a,
  avatarClass,
}) => {
  const waited = waitingDuration(a.waitingSince);
  const days = waitedDays(a.waitingSince);
  const stale = days !== null && days > LONG_WAIT_DAYS;
  const attempts = a.checkAttempts ?? 0;

  return (
    <li className="flex items-start gap-3 py-3">
      <span
        aria-hidden="true"
        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tracking-wide shadow-sm ${avatarClass}`}
      >
        {initials(a.fullName)}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate text-sm font-semibold leading-snug text-slate-900">
            {a.fullName}
          </span>
          {waited && (
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums ${
                stale ? "bg-amber-100 text-amber-800" : "text-slate-400"
              }`}
              title={`Waiting since ${new Date(a.waitingSince!).toLocaleDateString("en-AU")}`}
            >
              {waited}
            </span>
          )}
        </div>

        {/*
          Role and state are separate chips rather than one run-on line. They are
          two different kinds of fact — one fixed, one changing — and the state is
          the one worth colouring, which is impossible while they share a sentence.
        */}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {/* AL-478 #2: every role this person holds, the case-holding one first.
              A chip each rather than a joined string, so the leading role reads as
              the material one instead of both sharing equal weight in a sentence. */}
          {roleLabelsFor(a).map((label, i) => (
            <span
              key={label}
              className={`rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ${
                i === 0
                  ? "bg-white/80 text-slate-600 ring-slate-200"
                  : "bg-transparent text-slate-500 ring-slate-200"
              }`}
            >
              {label}
            </span>
          ))}
          <span
            className={`rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ${
              STATE_TONES[a.state] ?? STATE_TONE_FALLBACK
            }`}
          >
            {STATE_LABELS[a.state] ?? a.state}
          </span>
          {/* More than one attempt means the check itself is going wrong for them,
              which is a different conversation from "please get on with it". */}
          {attempts > 1 && (
            <span className="rounded-md bg-rose-100 px-1.5 py-0.5 text-[11px] font-medium text-rose-700 ring-1 ring-rose-200">
              {attempts} attempts
            </span>
          )}
        </div>

        {/* A person on a sub-case is not on the case the C1 opened, so say where
            they came from — otherwise an unfamiliar name looks like a mistake. */}
        {(a.depth ?? 0) > 0 && a.viaCaseName && (
          <p className="mt-1 text-[11px] text-slate-400">via {a.viaCaseName}</p>
        )}

        {a.email && (
          <a
            href={`mailto:${a.email}`}
            className="mt-1 inline-block text-xs font-medium text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-800 hover:decoration-blue-500"
          >
            {a.email}
          </a>
        )}
      </div>
    </li>
  );
};

/**
 * Group heading: a coloured dot, sentence-case label, and the count as a pill.
 *
 * The dot carries the group's colour into the white groups too, so "with us" and
 * "finished" are still distinguishable from each other without needing a tinted
 * card each.
 */
const GroupHeading: React.FC<{
  heading: string;
  count: number;
  note?: string;
  dotClass: string;
}> = ({ heading, count, note, dotClass }) => (
  <>
    <div className="flex items-center gap-2">
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
      <h4 className="text-[13px] font-bold tracking-tight text-slate-800">{heading}</h4>
      <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200 tabular-nums">
        {count}
      </span>
    </div>
    {note && <p className="mt-1 pl-4 text-[11px] leading-relaxed text-slate-500">{note}</p>}
  </>
);

interface Props {
  /** The parent case identifier. */
  caseIdentifier: string;
  /** Compact form for the in-progress card; the fuller form is for a page section. */
  compact?: boolean;
}

const CheckAccountsPanel: React.FC<Props> = ({ caseIdentifier, compact = false }) => {
  const [data, setData] = useState<CheckAccountsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchCheckAccounts(caseIdentifier)
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Deliberately not silent. If this fails the C1 must know they are looking at nothing,
        // rather than reading an empty list as "everyone is done".
        setError(e instanceof Error ? e.message : "Could not load progress detail");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [caseIdentifier]);

  if (loading) {
    return (
      <p className="text-sm text-gray-500" role="status" aria-live="polite">
        Checking who this case is waiting on…
      </p>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-amber-700" role="alert">
        Progress detail is unavailable ({error}). This does not mean the case is complete.
      </p>
    );
  }

  if (!data || data.totalPeople === 0) {
    return (
      <p className="text-sm text-gray-500">
        No additional people are required for this case.
      </p>
    );
  }

  const chaseable = data.outstandingPeople;
  // AL-456: approved users are outstanding but do not hold the case, so the headline must not imply the
  // case is stuck when they are all that is left. Derived locally as well as read from the response so the
  // heading stays right against an older backend that does not send the field.
  const pendingApproved =
    typeof data.pendingApprovedUsers === "number"
      ? data.pendingApprovedUsers
      : data.accounts.filter((a) => !holdsUpTheCase(a) && a.waitingOn !== "NOBODY").length;

  /**
   * AL-478 #1: how many people are genuinely with us.
   *
   * Read from the rows rather than inferred from the absence of anything else,
   * which is what let the panel claim work was with ARCaml on a case where the
   * ARCaml bucket was empty.
   */
  const withUs = data.accounts.filter((a) => a.waitingOn === "ARCAML").length;

  const headline = () => {
    if (chaseable > 0) {
      return `Waiting on ${chaseable} of ${data.totalPeople} ${
        data.totalPeople === 1 ? "person" : "people"
      }`;
    }
    if (pendingApproved > 0) {
      return `Nothing is holding this case up`;
    }
    return `All ${data.totalPeople} ${data.totalPeople === 1 ? "person" : "people"} accounted for`;
  };

  /**
   * AL-456: deliberately says nothing positional.
   *
   * This previously read "until the people below have completed their checks", which contradicted the
   * approved-user section immediately underneath saying those people do not hold the case up. Whenever a
   * case had BOTH a real blocker and an outstanding approved user, the panel argued with itself.
   *
   * So it counts the blockers explicitly, and where approved users also exist it reconciles the two in one
   * sentence rather than leaving the reader to work out which section the first claim applied to.
   */
  const subheading = () => {
    if (chaseable > 0) {
      const blockers =
        `This case cannot finish until the ${chaseable} outstanding ` +
        `${chaseable === 1 ? "person has" : "people have"} completed their checks.`;
      if (pendingApproved > 0) {
        return (
          blockers +
          ` A further ${pendingApproved} ${pendingApproved === 1 ? "person needs" : "people need"} to ` +
          `complete before they can use the system, but ${pendingApproved === 1 ? "does" : "do"} not ` +
          `hold the case up.`
        );
      }
      return blockers;
    }
    if (pendingApproved > 0) {
      // The distinction that was getting lost: these people are outstanding, and the case can still finish.
      return (
        `${pendingApproved} ${pendingApproved === 1 ? "person has" : "people have"} yet to complete ` +
        `their checks, but they do not block the case.`
      );
    }
    /**
     * AL-478 #1: only claim work is with us when somebody is actually in the
     * ARCaml bucket.
     *
     * This used to be an unconditional return, reached whenever nothing was
     * chaseable and no approved user was pending — regardless of whether any row
     * had `waitingOn === "ARCAML"`. On a finished case the headline said "All N
     * people accounted for" and this line said we still owed work, one line
     * apart.
     *
     * Approved users sitting at ARCAML are already handled by the branch above,
     * so these counts cannot double up.
     */
    if (withUs > 0) {
      return "Remaining work is with ARCaml — nothing is outstanding from your side.";
    }
    return "All checks on this case are complete — nothing is outstanding.";
  };

  return (
    <section aria-label="People this case is waiting on" className="text-left">
      {/*
        Headline and subheading only. The group headings below already name who
        the reader can act on, and the earlier version stated the same idea four
        times — headline, subheading, group heading, group note — before reaching
        an actual person. The group notes are now one clause each for the same
        reason.
      */}
      <h3 className="text-base font-bold tracking-tight text-slate-900">{headline()}</h3>
      <p className="mt-1 mb-4 text-xs leading-relaxed text-slate-500">{subheading()}</p>

      {GROUPS.map((group) => {
        // Approved users are rendered above under their own heading, so they are excluded here — the
        // groups below are about who is holding the case up.
        const rows = data.accounts.filter(
          (a) => a.waitingOn === group.key && (holdsUpTheCase(a) || a.waitingOn === "NOBODY"),
        );
        if (rows.length === 0) return null;
        // In the compact form, finished people are noise — the question is who is blocking.
        if (compact && group.key === "NOBODY") return null;

        return (
          <div key={group.key} className={`mb-3 rounded-xl p-3.5 shadow-sm ${group.card}`}>
            <GroupHeading
              heading={group.heading}
              count={rows.length}
              note={group.note}
              dotClass={group.dot}
            />
            <ul className="mt-2 divide-y divide-slate-200/70">
              {rows.map((a, i) => (
                <PersonRow key={`${a.fullName}-${i}`} account={a} avatarClass={group.avatar} />
              ))}
            </ul>
          </div>
        );
      })}

      {/*
        AL-456: approved users get their own heading rather than sitting under "waiting on your client's
        people". They are outstanding, and worth showing, but they do not hold the case — so grouping them
        with people who do told the C1 something untrue about what was stopping their case finishing.

        Rendered AFTER the blocking groups deliberately. It used to sit above them, which put the least
        actionable people first and made the summary's wording read as though it applied to them.
      */}
      {(() => {
        const pendingApprovedUsers = data.accounts.filter(
          (a) => !holdsUpTheCase(a) && a.waitingOn !== "NOBODY",
        );
        if (pendingApprovedUsers.length === 0) return null;
        return (
          <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
            <GroupHeading
              heading="Not yet approved to use the system"
              count={pendingApprovedUsers.length}
              note="They do not hold this case up — it can finish without them."
              dotClass="bg-violet-400"
            />
            <ul className="mt-2 divide-y divide-slate-200/70">
              {pendingApprovedUsers.map((a, i) => (
                <PersonRow
                  key={`au-${a.fullName}-${i}`}
                  account={a}
                  avatarClass="bg-gradient-to-br from-violet-400 to-purple-500 text-white"
                />
              ))}
            </ul>
          </div>
        );
      })()}

      {data.hasDeeperCases && (
        <p className="text-xs text-gray-400">
          Some of these people belong to a related entity on this case rather than to the entity
          itself.
        </p>
      )}
    </section>
  );
};

export default CheckAccountsPanel;
